import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DollarSign, Banknote, CreditCard, Trash2 } from 'lucide-react';

export function AdminChargeConfirmModal({
  isOpen,
  activeBooking,
  initialTotal,
  initialCash,
  initialTransfer,
  onClose,
  onConfirm,
  onUnmarkPaid
}: any) {
  const [total, setTotal] = useState(initialTotal);
  const [cash, setCash] = useState(initialCash);
  const [transfer, setTransfer] = useState(initialTransfer);

  useEffect(() => {
    if (isOpen) {
      setTotal(initialTotal);
      setCash(initialCash);
      setTransfer(initialTransfer);
    }
  }, [isOpen, initialTotal, initialCash, initialTransfer]);

  if (!isOpen || !activeBooking) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-[#1a1c23] border border-lime-500/30 rounded-2xl p-6 max-w-sm w-full space-y-5 shadow-2xl shadow-lime-500/10 animate-in zoom-in-95 duration-200 text-center">
        <div className="w-16 h-16 rounded-full bg-lime-500/10 flex items-center justify-center border border-lime-500/20 mx-auto">
          <DollarSign size={28} className="text-lime-500" />
        </div>
        
        <div>
          <h3 className="text-lg font-black text-white">
            {activeBooking?.status === 'paid' ? 'Modificar Cobro' : 'Cerrar / Registrar Turno'}
          </h3>
        </div>

        <div className="flex flex-col gap-3 text-left">
          <div>
            <label className="text-[10px] font-black uppercase text-zinc-400 mb-2 flex items-center justify-center gap-1.5">
              <DollarSign size={14} /> Total
            </label>
            <div className={`w-[80%] mx-auto bg-[#222530] border rounded-xl py-2.5 transition-colors flex justify-center ${activeBooking?.status === 'paid' ? 'border-lime-500/50 focus-within:border-lime-500 shadow-[0_0_15px_rgba(132,204,22,0.1)]' : 'border-white/10 opacity-70 cursor-not-allowed'}`}>
              <div className="flex items-center text-lg">
                <span className={`font-bold mr-1 select-none pointer-events-none ${activeBooking?.status === 'paid' ? 'text-zinc-500' : 'text-zinc-600'}`}>$</span>
                <div className="relative flex items-center">
                  <span className="invisible whitespace-pre font-bold pointer-events-none min-w-[20px]">
                    {total ? parseInt(total).toLocaleString('es-AR') : '0'}
                  </span>
                  <input
                    type="tel"
                    disabled={activeBooking?.status !== 'paid'}
                    value={total ? parseInt(total).toLocaleString('es-AR') : ''}
                    onChange={(e) => {
                      const numericVal = e.target.value.replace(/\D/g, '');
                      setTotal(numericVal);
                    }}
                    onBlur={() => {
                      const totalNum = parseInt(total || '0');
                      let c = parseInt(cash || '0');
                      let t = parseInt(transfer || '0');
                      if (c >= t) {
                        c = totalNum - t;
                        if (c < 0) { c = 0; t = totalNum; }
                      } else {
                        t = totalNum - c;
                        if (t < 0) { t = 0; c = totalNum; }
                      }
                      setCash(c.toString());
                      setTransfer(t.toString());
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const totalNum = parseInt(total || '0');
                        let c = parseInt(cash || '0');
                        let t = parseInt(transfer || '0');
                        if (c >= t) {
                          c = totalNum - t;
                          if (c < 0) { c = 0; t = totalNum; }
                        } else {
                          t = totalNum - c;
                          if (t < 0) { t = 0; c = totalNum; }
                        }
                        setCash(c.toString());
                        setTransfer(t.toString());
                        e.currentTarget.blur();
                      }
                    }}
                    className={`absolute left-0 w-full bg-transparent font-bold focus:outline-none text-center ${activeBooking?.status === 'paid' ? 'text-white' : 'text-zinc-400 cursor-not-allowed'}`}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] font-black uppercase text-zinc-400 mb-2 flex items-center justify-center gap-1.5">
                <Banknote size={14} /> Efectivo
              </label>
              <div className="w-full bg-[#222530] border border-white/10 rounded-xl py-2.5 focus-within:border-lime-500/50 transition-colors flex justify-center">
                <div className="flex items-center text-lg">
                  <span className="text-zinc-500 font-bold mr-1 select-none pointer-events-none">$</span>
                  <div className="relative flex items-center">
                    <span className="invisible whitespace-pre font-bold pointer-events-none min-w-[20px]">
                      {cash ? parseInt(cash).toLocaleString('es-AR') : '0'}
                    </span>
                    <input
                      type="tel"
                      value={cash ? parseInt(cash).toLocaleString('es-AR') : ''}
                      onChange={(e) => {
                        const numericVal = e.target.value.replace(/\D/g, '');
                        setCash(numericVal);
                      }}
                      onBlur={() => {
                        const totalNum = parseInt(total || '0');
                        let numVal = parseInt(cash || '0');
                        if (numVal > totalNum) {
                          numVal = totalNum;
                          setCash(numVal.toString());
                        }
                        if (cash !== '') {
                          setTransfer((totalNum - numVal).toString());
                        } else {
                          setTransfer(totalNum.toString());
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const totalNum = parseInt(total || '0');
                          let numVal = parseInt(cash || '0');
                          if (numVal > totalNum) {
                            numVal = totalNum;
                            setCash(numVal.toString());
                          }
                          if (cash !== '') {
                            setTransfer((totalNum - numVal).toString());
                          } else {
                            setTransfer(totalNum.toString());
                          }
                          e.target.blur();
                        }
                      }}
                      className="absolute left-0 w-full bg-transparent text-white font-bold focus:outline-none text-center"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1">
              <label className="text-[10px] font-black uppercase text-zinc-400 mb-2 flex items-center justify-center gap-1.5">
                <CreditCard size={14} /> Transferencia
              </label>
              <div className="w-full bg-[#222530] border border-white/10 rounded-xl py-2.5 focus-within:border-lime-500/50 transition-colors flex justify-center">
                <div className="flex items-center text-lg">
                  <span className="text-zinc-500 font-bold mr-1 select-none pointer-events-none">$</span>
                  <div className="relative flex items-center">
                    <span className="invisible whitespace-pre font-bold pointer-events-none min-w-[20px]">
                      {transfer ? parseInt(transfer).toLocaleString('es-AR') : '0'}
                    </span>
                    <input
                      type="tel"
                      value={transfer ? parseInt(transfer).toLocaleString('es-AR') : ''}
                      onChange={(e) => {
                        const numericVal = e.target.value.replace(/\D/g, '');
                        setTransfer(numericVal);
                      }}
                      onBlur={() => {
                        const totalNum = parseInt(total || '0');
                        let numVal = parseInt(transfer || '0');
                        if (numVal > totalNum) {
                          numVal = totalNum;
                          setTransfer(numVal.toString());
                        }
                        if (transfer !== '') {
                          setCash((totalNum - numVal).toString());
                        } else {
                          setCash(totalNum.toString());
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const totalNum = parseInt(total || '0');
                          let numVal = parseInt(transfer || '0');
                          if (numVal > totalNum) {
                            numVal = totalNum;
                            setTransfer(numVal.toString());
                          }
                          if (transfer !== '') {
                            setCash((totalNum - numVal).toString());
                          } else {
                            setCash(totalNum.toString());
                          }
                          e.target.blur();
                        }
                      }}
                      className="absolute left-0 w-full bg-transparent text-white font-bold focus:outline-none text-center"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={onClose}
            className="flex-1 bg-[#222530] hover:bg-[#2d313f] text-zinc-300 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              const cashNum = parseInt(cash || '0');
              const transferNum = parseInt(transfer || '0');
              const targetTotal = parseInt(total || '0');
              if (cashNum + transferNum !== targetTotal) {
                alert('La suma de efectivo y transferencia debe coincidir con el total a cobrar.');
                return;
              }
              onConfirm(cashNum, transferNum, targetTotal);
            }}
            className="flex-[1.5] bg-[#84cc16] hover:bg-[#a3e635] text-[#022c22] py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-lime-500/20 cursor-pointer"
          >
            Confirmar
          </button>
        </div>

        {activeBooking.status === 'paid' && (
          <div className="pt-2 flex justify-center">
            <button
              type="button"
              onClick={onUnmarkPaid}
              className="w-2/3 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 shadow-sm"
            >
              <Trash2 size={12} /> Anular Cobro
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
